import axios from 'axios';

let legacyInterceptorInstalled = false;


/** Backend base (no trailing slash). Set REACT_APP_BACKEND_URL on Vercel at build time. */
function getBackendBase() {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  if (raw) return raw;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname || '';
    if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:8001';
    // Prod bundle sometimes ships without env; same-origin /api/* would hit Vercel (HTML)
    if (h.includes('vercel.app') || h.endsWith('.vercel.app')) {
      return 'https://r3vit-production.up.railway.app';
    }
  }
  return 'http://localhost:8001';
}

const API = `${getBackendBase()}/api`;

// Cache for presigned URLs to avoid excessive API calls
// Key: file_path, Value: { url, expiresAt }
const urlCache = new Map();

// Cache duration slightly less than server expiry (2.5 min vs 3 min)
const CACHE_DURATION_MS = 150000;

/**
 * Get a presigned download URL for a file.
 * Uses caching to avoid repeated API calls for the same file.
 * @param {string} filePath - The R2 file path (e.g., "r3vit/sidequests/...")
 * @returns {Promise<string>} The presigned URL
 */
export async function getPresignedDownloadUrl(filePath) {
  // Check cache first
  const cached = urlCache.get(filePath);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  try {
    const encodedPath = encodeURIComponent(filePath).replace(/%2F/g, '/');
    const response = await axios.get(`${API}/presigned-url/download/${encodedPath}`);
    const { url, expires_in } = response.data;
    
    // Cache the URL
    urlCache.set(filePath, {
      url,
      expiresAt: Date.now() + (expires_in * 1000) - 30000 // Expire 30s early for safety
    });
    
    return url;
  } catch (error) {
    console.error('Failed to get presigned download URL:', error);
    throw error;
  }
}

/**
 * Get a presigned upload URL for uploading a file directly to R2.
 * @param {Object} params
 * @param {string} params.fileType - 'sidequest' or 'business'
 * @param {string} params.targetId - The sidequest_id or business_id
 * @param {string} params.contentType - MIME type (e.g., 'image/jpeg')
 * @param {string} params.fileExtension - File extension (e.g., 'jpg')
 * @returns {Promise<{uploadUrl: string, filePath: string}>}
 */
export async function getPresignedUploadUrl({ fileType, targetId, contentType, fileExtension }) {
  try {
    const response = await axios.post(`${API}/presigned-url/upload`, {
      file_type: fileType,
      target_id: targetId,
      content_type: contentType,
      file_extension: fileExtension
    });
    
    return {
      uploadUrl: response.data.upload_url,
      filePath: response.data.file_path,
      expiresIn: response.data.expires_in
    };
  } catch (error) {
    console.error('Failed to get presigned upload URL:', error);
    throw error;
  }
}

/**
 * Upload a file directly to R2 using a presigned URL.
 * @param {string} presignedUrl - The presigned upload URL
 * @param {File} file - The file to upload
 * @param {string} contentType - The content type
 * @param {function} onProgress - Optional progress callback (0-100)
 * @returns {Promise<void>}
 */
export async function uploadToR2(presignedUrl, file, contentType, onProgress) {
  try {
    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': contentType
      },
      // Don't send auth headers to R2
      transformRequest: [(data, headers) => {
        delete headers.Authorization;
        return data;
      }],
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          onProgress(percent);
        }
      }
    });
  } catch (error) {
    console.error('Failed to upload to R2:', error);
    throw error;
  }
}

/**
 * Confirm that an upload was completed and update the database.
 * @param {Object} params
 * @param {string} params.fileType - 'sidequest' or 'business'
 * @param {string} params.filePath - The R2 file path returned from presigned upload
 * @param {string} params.targetId - The sidequest_id or business_id
 * @param {string} params.contentType - MIME type
 * @param {string} params.caption - Optional caption (for sidequests)
 * @param {string} params.thumbnailPath - Optional thumbnail path (for sidequest video uploads)
 * @returns {Promise<Object>} The server response
 */
export async function confirmUpload({ fileType, filePath, targetId, contentType, caption = '', thumbnailPath = '' }) {
  try {
    const formData = new FormData();
    formData.append('file_type', fileType);
    formData.append('file_path', filePath);
    formData.append('target_id', targetId);
    formData.append('content_type', contentType);
    formData.append('caption', caption);
    if (thumbnailPath) {
      formData.append('thumbnail_path', thumbnailPath);
    }

    const response = await axios.post(`${API}/presigned-url/confirm-upload`, formData);
    return response.data;
  } catch (error) {
    console.error('Failed to confirm upload:', error);
    throw error;
  }
}

/**
 * Complete upload flow: get presigned URL, upload to R2, confirm upload.
 * @param {Object} params
 * @param {string} params.fileType - 'sidequest' or 'business'
 * @param {string} params.targetId - The sidequest_id or business_id
 * @param {File} params.file - The file to upload
 * @param {string} params.caption - Optional caption
 * @param {Blob} params.thumbnailBlob - Optional thumbnail blob for video uploads
 * @param {function} params.onProgress - Optional progress callback
 * @returns {Promise<Object>} The server response from confirm
 */
export async function uploadWithPresignedUrl({ fileType, targetId, file, caption = '', thumbnailBlob = null, onProgress }) {
  // Get file info
  const contentType = file.type || 'application/octet-stream';
  const fileExtension = file.name.split('.').pop() || (contentType.startsWith('video/') ? 'mp4' : 'jpg');
  const isVideo = contentType.startsWith('video/');

  // Step 1: Get presigned upload URL for main file
  if (onProgress) onProgress(5);
  const { uploadUrl, filePath } = await getPresignedUploadUrl({
    fileType,
    targetId,
    contentType,
    fileExtension
  });

  // Step 1b: If video and thumbnail provided, get presigned URL for thumbnail
  let thumbnailPath = '';
  let thumbnailUploadUrl = '';
  if (isVideo && thumbnailBlob) {
    const thumbResponse = await getPresignedUploadUrl({
      fileType: 'sidequest-thumbnail',
      targetId,
      contentType: 'image/jpeg',
      fileExtension: 'jpg'
    });
    thumbnailUploadUrl = thumbResponse.uploadUrl;
    thumbnailPath = thumbResponse.filePath;
  }

  // Step 2: Upload main file directly to R2
  await uploadToR2(uploadUrl, file, contentType, (percent) => {
    if (onProgress) {
      // Map 0-100 to 10-80 (leave room for thumbnail upload)
      const maxProgress = thumbnailBlob ? 70 : 90;
      onProgress(10 + Math.round(percent * (maxProgress - 10) / 100));
    }
  });

  // Step 2b: Upload thumbnail if provided
  if (isVideo && thumbnailBlob && thumbnailUploadUrl) {
    if (onProgress) onProgress(75);
    await uploadToR2(thumbnailUploadUrl, thumbnailBlob, 'image/jpeg', (percent) => {
      if (onProgress) {
        // Map 0-100 to 75-90
        onProgress(75 + Math.round(percent * 0.15));
      }
    });
  }

  // Step 3: Confirm upload with backend
  if (onProgress) onProgress(95);
  const result = await confirmUpload({
    fileType,
    filePath,
    targetId,
    contentType,
    caption,
    thumbnailPath
  });

  if (onProgress) onProgress(100);
  return result;
}

/**
 * Intercept legacy direct upload endpoints and reroute to presigned upload flow.
 * This prevents files from being sent to the backend even if older code paths are used.
 */
export function setupLegacyUploadInterceptor() {
  if (legacyInterceptorInstalled) return;
  legacyInterceptorInstalled = true;

  axios.interceptors.request.use(async (config) => {
    const method = (config.method || 'get').toLowerCase();
    const url = config.url || '';
    if (method !== 'post' || !url) return config;

    const sidequestMatch = url.match(/\/api\/sidequests\/([^/]+)\/submit/);
    const businessMatch = url.match(/\/api\/businesses\/([^/]+)\/upload-image/);

    if (!sidequestMatch && !businessMatch) return config;

    if (!(config.data instanceof FormData)) return config;

    const file = config.data.get('file');
    if (!file) return config;

    const fileType = sidequestMatch ? 'sidequest' : 'business';
    const targetId = sidequestMatch ? sidequestMatch[1] : businessMatch[1];
    const caption = sidequestMatch ? (config.data.get('caption') || '') : '';

    config.adapter = async () => {
      const result = await uploadWithPresignedUrl({
        fileType,
        targetId,
        file,
        caption
      });

      return {
        data: result,
        status: 200,
        statusText: 'OK',
        headers: {},
        config
      };
    };

    return config;
  }, (error) => Promise.reject(error));
}

/**
 * Get the appropriate media URL for display.
 * For /api/r2/ paths, fetches a presigned URL.
 * For other URLs, returns as-is.
 * @param {string} url - The stored URL
 * @returns {Promise<string>} The URL to use for display
 */
export async function getMediaUrl(url) {
  if (!url) return '';

  const base = getBackendBase();

  // R2 object key from various stored formats (all hit presigned GET — never same-origin /api/* on Vercel)
  let r2Key = null;
  if (url.startsWith('/api/r2/')) {
    r2Key = url.replace('/api/r2/', '');
  } else if (url.startsWith('/api/business-files/')) {
    r2Key = url.replace('/api/business-files/', '');
  } else if (url.startsWith('/api/sidequest-files/')) {
    r2Key = url.replace('/api/sidequest-files/', '');
  } else if (url.includes('/api/business-files/')) {
    const i = url.indexOf('/api/business-files/');
    r2Key = url.slice(i + '/api/business-files/'.length).split('?')[0];
  } else if (url.includes('/api/sidequest-files/')) {
    const i = url.indexOf('/api/sidequest-files/');
    r2Key = url.slice(i + '/api/sidequest-files/'.length).split('?')[0];
  } else if (url.includes('/api/r2/')) {
    const i = url.indexOf('/api/r2/');
    r2Key = url.slice(i + '/api/r2/'.length).split('?')[0];
  }

  if (r2Key) {
    return await getPresignedDownloadUrl(r2Key);
  }

  // Any other /api/* on backend (avoid relative URL when env missing)
  if (url.startsWith('/api/')) {
    return `${base}${url}`;
  }

  // Bare R2 object key (no http(s):// and no leading /), e.g. "r3vit/events/xyz/abc.jpg"
  // Stored directly by the presigned-upload flow for event images & sidequest thumbnails.
  if (!/^https?:\/\//i.test(url) && !url.startsWith('/') && !url.startsWith('data:') && !url.startsWith('blob:')) {
    return await getPresignedDownloadUrl(url);
  }

  return url;
}

/**
 * Clear the URL cache (useful when user logs out or for testing)
 */
export function clearUrlCache() {
  urlCache.clear();
}
